
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserAccess, UserType } from "@/types/user";
// Update import to use the consolidated AuthContext
import { useAuth } from "@/auth/context/AuthContext";
import { AuthService } from "@/auth/services/authService";

export const useUserAccess = () => {
  const { toast } = useToast();
  const { user } = useAuth();

  const checkUserAccess = async (): Promise<UserAccess | null> => {
    try {
      // Use user from auth context
      if (!user) {
        console.log("User not authenticated in checkUserAccess");
        return null;
      }
      
      // Normalize the email address to ensure consistent comparison
      const normalizedEmail = user.email?.trim().toLowerCase();
      
      // Debug log to help troubleshoot admin recognition issues
      console.log(`Email check in useUserAccess: '${normalizedEmail}'`);
      
      // Use our centralized AuthService to check if user is admin
      const isAdminByEmail = AuthService.isAdmin(user);
      console.log(`Is admin email? ${isAdminByEmail}`);

      // Get user profile to check type
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('user_type, stripe_customer_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        // If we can't get the profile but we know it's an admin email, still grant admin access
        if (isAdminByEmail) {
          console.log('Profile fetch failed but admin email detected, granting admin access');
          return {
            user,
            isAdmin: true,
            userType: 'admin',
            reportCount: 0,
            usedFreeReport: false,
            stripeCustomerId: null
          };
        }
        // Don't throw an error, just return null to indicate access check failed
        // but this shouldn't clear the session
        return null;
      }

      // Additional debug logging
      console.log('Profile data from DB:', profileData);
      
      // Normalize user type to ensure it's valid
      let userType = profileData?.user_type || 'free';
      
      // Check if user_type in profile is already admin
      const isAdminByType = userType === 'admin';
      
      console.log(`Admin check - by email: ${isAdminByEmail}, by user_type: ${isAdminByType}`);
      
      // Determine final admin status - either by email OR by stored user_type
      const isAdmin = isAdminByEmail || isAdminByType;
      
      // If admin by email but not by type, we should update the profile
      if (isAdminByEmail && !isAdminByType) {
        console.log('Admin email detected but profile not set as admin, updating profile...');
        try {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ user_type: 'admin', updated_at: new Date().toISOString() })
            .eq('id', user.id);
            
          if (updateError) {
            console.error('Failed to update profile to admin:', updateError);
          } else {
            console.log('Successfully updated profile to admin');
            userType = 'admin';
          }
        } catch (err) {
          console.error('Exception updating profile to admin:', err);
        }
      }
      
      // Validate against the allowed UserType values
      const validUserTypes: UserType[] = ['admin', 'free', 'starter', 'pro', 'premium', 'standard', 'analyst'];
      
      if (!validUserTypes.includes(userType as UserType)) {
        console.log(`Invalid user_type "${userType}" found, normalizing to "free"`);
        userType = 'free';
      }

      // Check if user exists in report_usage table
      const { data: reportUsage, error: reportUsageError } = await supabase
        .from('report_usage')
        .select('reports_generated')
        .eq('user_id', user.id)
        .single();
      
      if (reportUsageError && reportUsageError.code !== 'PGRST116') { // Ignore not found error
        console.error('Error fetching report usage:', reportUsageError);
        // Continue anyway with default values
      }

      // Use reports_generated from report_usage, or fallback to 0
      const reportCount = reportUsage ? reportUsage.reports_generated : 0;
      
      return {
        user,
        isAdmin,
        userType: isAdmin ? 'admin' : userType as UserType,
        reportCount,
        usedFreeReport: reportCount >= 2,
        stripeCustomerId: profileData?.stripe_customer_id || null
      };
    } catch (error) {
      console.error('Error checking user access:', error);
      toast({
        title: "Error",
        description: "Failed to check user access. Please try again.",
        variant: "destructive",
      });
      
      // Return null instead of throwing, so this doesn't force a session clear
      return null;
    }
  };

  const markFreeReportUsed = async () => {
    try {
      if (!user) {
        console.log('Not authenticated in markFreeReportUsed');
        return false;
      }

      console.log('Marking report usage increment for user:', user.id);
      
      // Check if user record exists in report_usage
      const { data: existingUsage, error: checkError } = await supabase
        .from('report_usage')
        .select('reports_generated')
        .eq('user_id', user.id)
        .single();
        
      if (checkError && checkError.code !== 'PGRST116') { // Not found is ok
        console.error('Error checking existing report usage:', checkError);
        // Continue anyway and try to create/update
      }
      
      if (existingUsage) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('report_usage')
          .update({ 
            reports_generated: existingUsage.reports_generated + 1,
            last_report_date: new Date().toISOString()
          })
          .eq('user_id', user.id);
          
        if (updateError) {
          console.error('Error updating report usage:', updateError);
          return false;
        }
      } else {
        // Create new record
        const { error: insertError } = await supabase
          .from('report_usage')
          .insert({ 
            user_id: user.id,
            reports_generated: 1,
            last_report_date: new Date().toISOString(),
            last_reset_at: new Date().toISOString()
          });
          
        if (insertError) {
          console.error('Error creating report usage:', insertError);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error marking free report as used:', error);
      // Don't show a toast here as it might be confusing to users
      return false;
    }
  };

  return { checkUserAccess, markFreeReportUsed };
};
